import React from 'react';
import { Mail, UserPlus, X } from 'lucide-react';
import { Participant, ParticipantRole } from '@/types/meeting';

interface AttendeeListProps {
  attendees: Participant[];
  onAddAttendee: (attendee: Participant) => void;
  onRemoveAttendee: (attendeeId: string) => void;
  onUpdateAttendance: (attendeeId: string, status: 'PRESENT' | 'ABSENT' | 'LATE') => void;
}

export function AttendeeList({ 
  attendees, 
  onAddAttendee, 
  onRemoveAttendee,
  onUpdateAttendance 
}: AttendeeListProps) {
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [newAttendee, setNewAttendee] = React.useState({
    name: '',
    email: '',
    role: 'TEACHER' as ParticipantRole
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAddAttendee({
      id: Math.random().toString(36).substr(2, 9),
      ...newAttendee
    } as Participant);
    setNewAttendee({ name: '', email: '', role: ParticipantRole.TEACHER });
    setShowAddForm(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Attendees</h3>
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <UserPlus className="w-4 h-4 mr-1" />
          Add Attendee
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 p-4 rounded-lg space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              value={newAttendee.name}
              onChange={(e) => setNewAttendee(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              value={newAttendee.email}
              onChange={(e) => setNewAttendee(prev => ({ ...prev, email: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Role</label>
            <select
              value={newAttendee.role}
              onChange={(e) => setNewAttendee(prev => ({ ...prev, role: e.target.value as Participant['role'] }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="TEACHER">Teacher</option>
              <option value="PARENT">Parent</option>
              <option value="STUDENT">Student</option>
              <option value="ADMIN">Administrator</option>
              <option value="COUNSELOR">Counselor</option>
              <option value="DEPARTMENT_HEAD">Department Head</option>
            </select>
          </div>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Add
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200">
        {attendees.map((attendee) => (
          <div key={attendee.id} className="p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src={attendee.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(attendee.name)}`}
                alt={attendee.name}
                className="w-10 h-10 rounded-full"
              />
              <div>
                <h4 className="text-sm font-medium text-gray-900">{attendee.name}</h4>
                <div className="flex items-center text-sm text-gray-500">
                  <Mail className="w-4 h-4 mr-1" />
                  {attendee.email}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <select
                onChange={(e) => onUpdateAttendance(attendee.id, e.target.value as 'PRESENT' | 'ABSENT' | 'LATE')}
                className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                defaultValue=""
              >
                <option value="" disabled>Status</option>
                <option value="PRESENT">Present</option>
                <option value="ABSENT">Absent</option>
                <option value="LATE">Late</option>
              </select>

              <button
                onClick={() => onRemoveAttendee(attendee.id)}
                className="p-1 text-gray-400 hover:text-red-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        ))}

        {attendees.length === 0 && (
          <div className="p-4 text-center text-gray-500">
            No attendees added yet
          </div>
        )}
      </div>
    </div>
  );
}